import { useMemo } from 'react';

const HEATMAP_MODES = {
  sales: {
    field: 'productAvgSales',
    label: 'Sales',
    positiveColor: [16, 185, 129],   // green
    negativeColor: [239, 68, 68],     // red
    neutralBg: '#f9fafb'
  },
  margin: {
    field: 'productMargin',
    label: 'Margin',
    positiveColor: [16, 185, 129],
    negativeColor: [239, 68, 68],
    neutralBg: '#f9fafb'
  },
  stock: {
    field: null, // computed from current_stock / total_stock_on_fillup
    label: 'Stock',
    positiveColor: [16, 185, 129],
    negativeColor: [239, 68, 68],
    neutralBg: '#f9fafb'
  },
  category: {
    field: 'productCategory',
    label: 'Category',
    neutralBg: '#f9fafb'
  }
};

// Pastel palette for categories (10 colors)
const CATEGORY_COLORS = [
  'rgba(59, 130, 246, 0.25)',   // blue
  'rgba(16, 185, 129, 0.25)',   // green
  'rgba(245, 158, 11, 0.25)',   // amber
  'rgba(239, 68, 68, 0.25)',    // red
  'rgba(139, 92, 246, 0.25)',   // purple
  'rgba(236, 72, 153, 0.25)',   // pink
  'rgba(6, 182, 212, 0.25)',    // cyan
  'rgba(234, 179, 8, 0.25)',    // yellow
  'rgba(249, 115, 22, 0.25)',   // orange
  'rgba(107, 114, 128, 0.25)'   // gray
];

const getSlotValue = (slot, mode) => {
  if (!slot || !slot.name) return null;

  if (mode === 'stock') {
    const total = slot.total_stock_on_fillup || 0;
    if (total <= 0) return null;
    return (slot.current_stock || 0) / total;
  }

  if (mode === 'sales') {
    return slot.salesDeltaPercentage ?? null;
  }

  if (mode === 'margin') {
    return slot.marginDeltaPercentage ?? null;
  }

  return null;
};

// Get absolute display value for a slot in a given mode
const getAbsoluteValue = (slot, mode) => {
  if (!slot || !slot.name) return null;

  if (mode === 'sales') {
    return slot.productAvgSales > 0 ? slot.productAvgSales : null;
  }

  if (mode === 'margin') {
    return slot.productMargin > 0 ? slot.productMargin : null;
  }

  if (mode === 'stock') {
    const total = slot.total_stock_on_fillup || 0;
    if (total <= 0) return null;
    return (slot.current_stock || 0) / total;
  }

  return null;
};

const useHeatmapColors = (slots, mode) => {
  return useMemo(() => {
    if (!slots || !mode || mode === 'none' || mode === 'products') {
      return { colorMap: {}, valueMap: {}, absoluteValueMap: {}, categoryMap: {}, minVal: 0, maxVal: 0 };
    }

    const config = HEATMAP_MODES[mode];
    if (!config) return { colorMap: {}, valueMap: {}, absoluteValueMap: {}, categoryMap: {}, minVal: 0, maxVal: 0 };

    const colorMap = {};
    const valueMap = {};
    const absoluteValueMap = {};
    const categoryMap = {};
    let minVal = Infinity;
    let maxVal = -Infinity;

    // Category mode: assign colors by productCategory
    if (mode === 'category') {
      const categories = new Set();
      slots.forEach(slot => {
        if (slot?.productCategory) categories.add(slot.productCategory);
      });

      const categoryColorIndex = {};
      let idx = 0;
      categories.forEach(cat => {
        categoryColorIndex[cat] = CATEGORY_COLORS[idx % CATEGORY_COLORS.length];
        idx++;
      });

      slots.forEach(slot => {
        const key = `${slot.row}-${slot.column}`;
        const cat = slot?.productCategory;
        if (cat) {
          colorMap[key] = categoryColorIndex[cat];
          categoryMap[key] = cat;
        } else {
          colorMap[key] = config.neutralBg;
          categoryMap[key] = null;
        }
        valueMap[key] = null;
        absoluteValueMap[key] = null;
      });

      // Build a legend-ready structure
      categoryMap._legend = Object.entries(categoryColorIndex).map(([name, color]) => ({ name, color }));

      return { colorMap, valueMap, absoluteValueMap, categoryMap, minVal: 0, maxVal: 0 };
    }

    if (mode === 'stock') {
      // Stock mode: 0-1 ratio, green=high, red=low
      slots.forEach(slot => {
        const key = `${slot.row}-${slot.column}`;
        const val = getSlotValue(slot, mode);
        const absVal = getAbsoluteValue(slot, mode);
        valueMap[key] = val;
        absoluteValueMap[key] = absVal;
        if (val === null) {
          colorMap[key] = config.neutralBg;
          return;
        }
        if (val < minVal) minVal = val;
        if (val > maxVal) maxVal = val;
        // val is 0..1, higher is better
        const intensity = Math.min(Math.max(val, 0), 1);
        if (intensity >= 0.5) {
          const alpha = 0.15 + ((intensity - 0.5) * 2) * 0.5;
          colorMap[key] = `rgba(${config.positiveColor.join(',')}, ${alpha.toFixed(2)})`;
        } else {
          const alpha = 0.15 + ((0.5 - intensity) * 2) * 0.5;
          colorMap[key] = `rgba(${config.negativeColor.join(',')}, ${alpha.toFixed(2)})`;
        }
      });
    } else {
      // Sales/Margin mode: delta percentage, positive=green, negative=red
      const values = slots
        .map(s => getSlotValue(s, mode))
        .filter(v => v !== null);

      if (values.length === 0) return { colorMap: {}, valueMap: {}, absoluteValueMap: {}, categoryMap: {}, minVal: 0, maxVal: 0 };

      const maxAbs = Math.max(...values.map(Math.abs), 1);

      slots.forEach(slot => {
        const key = `${slot.row}-${slot.column}`;
        const val = getSlotValue(slot, mode);
        const absVal = getAbsoluteValue(slot, mode);
        valueMap[key] = val;
        absoluteValueMap[key] = absVal;
        if (val === null) {
          colorMap[key] = config.neutralBg;
          return;
        }
        if (val < minVal) minVal = val;
        if (val > maxVal) maxVal = val;
        const normalized = Math.min(Math.abs(val) / maxAbs, 1);
        const alpha = 0.15 + normalized * 0.55;
        const rgb = val >= 0 ? config.positiveColor : config.negativeColor;
        colorMap[key] = `rgba(${rgb.join(',')}, ${alpha.toFixed(2)})`;
      });
    }

    // Fix infinity values when no valid data
    if (minVal === Infinity) minVal = 0;
    if (maxVal === -Infinity) maxVal = 0;

    return { colorMap, valueMap, absoluteValueMap, categoryMap, minVal, maxVal };
  }, [slots, mode]);
};

export { HEATMAP_MODES };
export default useHeatmapColors;
